import React, { useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import Dither from './Dither';
import { Link } from 'react-router-dom';

export default function AbrahamBio() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Custom CSS for animations and patterns
    const dossierStyles = `
        .glass-morphism {
            background: rgba(255, 255, 255, 0.03);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .text-glow {
            text-shadow: 0 0 30px rgba(56, 189, 248, 0.3);
        }
        .font-heading {
            font-family: 'Heading Now', sans-serif;
            font-weight: 800;
        }
    `;

    const { scrollYProgress } = useScroll();
    const yHero = useTransform(scrollYProgress, [0, 1], [0, 250]);
    const opacityHero = useTransform(scrollYProgress, [0, 0.4], [1, 0]);
    const scaleHero = useTransform(scrollYProgress, [0, 0.4], [1, 0.95]);

    // --- CINEMATIC EDITORIAL COMPONENTS ---
    
    const AmbientGlow = ({ color = "#38bdf8", opacity = 0.1, size = "500px" }) => (
        <div 
            className="absolute pointer-events-none blur-[120px] rounded-full z-0"
            style={{ 
                backgroundColor: color, 
                opacity, 
                width: size, 
                height: size,
                transition: "all 1s ease-in-out"
            }}
        />
    );

    const GlassGalleryCard = ({ title, desc, url, delay = 0, wide = false }) => {
        const isPPT = url?.endsWith('.pptx');
        
        return (
            <motion.div 
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1], delay }}
                className={`group relative flex flex-col glass-morphism rounded-2xl overflow-hidden hover:bg-white/[0.06] transition-all duration-700 ${wide ? 'md:col-span-2' : ''}`}
            >
                {/* Media Preview */}
                <div className="relative aspect-[16/9] w-full bg-black/40 overflow-hidden">
                    {isPPT ? (
                        <div className="w-full h-full flex flex-col items-center justify-center p-12 text-center group-hover:scale-105 transition-transform duration-1000">
                             <div className="w-20 h-20 rounded-full bg-white/5 flex items-center justify-center mb-6">
                                <svg className="w-8 h-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                             </div>
                             <h4 className="font-heading text-2xl uppercase tracking-tighter text-white mb-6">INTERACTIVE_SLIDE_DECK</h4>
                             <a href={url} target="_blank" rel="noreferrer" className="px-10 py-3 bg-accent text-black font-mono text-[10px] font-black uppercase tracking-[0.2em] rounded-full hover:scale-110 transition-all">REVIEW PRESENTATION</a>
                        </div>
                    ) : (
                        <iframe 
                            src={url}
                            className="w-full h-full border-none opacity-80 group-hover:opacity-100 transition-opacity duration-1000 pointer-events-none md:pointer-events-auto"
                            title={title}
                        />
                    )}
                    {/* Artistic Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                </div>

                {/* Content Info */}
                <div className="p-8 md:p-10 flex flex-col gap-4">
                    <div className="flex items-center gap-3">
                        <span className="w-8 h-px bg-accent/40"></span>
                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-accent/60">GALLERY_STOCKED</span>
                    </div>
                    <h3 className="font-heading text-3xl md:text-4xl text-white uppercase tracking-tighter leading-none mb-2">{title}</h3>
                    <p className="font-mono text-xs text-muted leading-relaxed max-w-xl opacity-60 group-hover:opacity-100 transition-opacity duration-700">
                        {desc}
                    </p>
                    <div className="flex items-center justify-between pt-8 mt-4 border-t border-white/5">
                        <span className="font-mono text-[9px] uppercase tracking-[0.4em] text-white/20">{url.split('/').pop()}</span>
                        <a href={url} download className="font-mono text-[10px] font-black text-white hover:text-accent uppercase tracking-widest flex items-center gap-2 group/link">
                            SECURE_ACCESS <span className="group-hover:translate-x-1 transition-transform">→</span>
                        </a>
                    </div>
                </div>
            </motion.div>
        );
    };

    const engineeringPortfolio = [
        {
            name: "TAB",
            type: "IOS APPLICATION",
            stack: ["Swift", "CoreData", "Speech API"],
            metrics: ["React Native", "Local Storage", "Voice Input"],
            description: "An offline-first iOS application to track IOUs and split expenses effortlessly using simple voice inputs.",
            link: "#"
        },
        {
            name: "HARVEST",
            type: "IOS APPLICATION",
            stack: ["SwiftUI", "Firebase", "MapKit"],
            metrics: ["Location Based", "Real-time Chat"],
            description: "A community platform connecting neighbors to buy, sell, and share locally grown produce via secure messaging.",
             link: "#"
        },
        {
            name: "COMANDO",
            type: "IOS AI ASSISTANT",
            stack: ["CoreML", "Swift", "RAG Arch"],
            metrics: ["100% Offline", "Zero Data Collection"],
            description: "A secure, fully offline AI assistant optimized for low-connectivity environments and high-speed local inference.",
             link: "#"
        },
         {
            name: "5 PANES 2 PECES",
            type: "WEB PLATFORM",
            stack: ["React", "Vercel", "Tailwind"],
            metrics: ["Community Portal", "Event Management"],
            description: "A digital sanctuary for community, faith, and outreach, connecting people through modern web experiences.",
            link: "https://www.5panes2peces.org/"
        },
        {
            name: "NEW HOPE",
            type: "WEB PLATFORM",
            stack: ["Next.js", "PostgreSQL", "Stripe"],
            metrics: ["Live Streaming", "Resource Hub"],
            description: "A comprehensive digital platform for connection, spiritual growth, and community support.",
             link: "https://newhope.life"
        }
    ];

    const seniorProject = {
        title: "DALEY WED",
        domain: "Premium Booking Platform",
        status: "LIVE_DEPLOYMENT",
        description: "A comprehensive digital portfolio and booking platform for wedding services. Features a custom CMS for client galleries, high-resolution image delivery, and elegant digital storytelling architecture.",
        url: "https://daleywed.com",
        technologies: ["React", "Custom CMS", "Vercel Architecture", "Responsive Grids"],
        deliverables: [
            "Student Journalist Portal",
            "High-Performance Image Delivery",
            "Brutalist Editorial Design System"
        ],
        documents: [
            { 
               title: "PROPOSAL REPORT & SLIDES", 
               desc: "Initial architectural proposal, defining the core problem space, target audience, and the technical requirements for the digital newsroom.",
               file: "/docs/Journalism_Proposal_Slides.pptx" 
            },
            { 
               title: "PROTOTYPE & ENGINEERING", 
               desc: "Mid-development engineering prototype outlining the database schemas, CMS integration, and brutalist UI wireframes.",
               file: "/docs/Engineering_Prototype_Slides.pptx" 
            }
        ]
    };

    const careerReadiness = [
        { category: "RESUME", status: "AVAILABLE", detail: "Professional Software Engineering & UI Design Resume.", file: "/docs/Abraham_Mora_Resume.pdf" },
        { category: "WORK-BASED LEARNING", status: "COMPLETED", detail: "Evidence of Professional Internships, Client Projects, and Service Hours." },
        { category: "CERTIFICATION", status: "VERIFIED", detail: "NVIDIA AI & MACHINE LEARNING CERTIFIED SPECIALIST", accent: true, file: "/docs/NVIDIA_Certificate.pdf" },
        { category: "COMMUNICATION", status: "OPTIMAL", detail: "Experience leading technical discussions and presenting architectures." },
        { category: "SYSTEMS DESIGN", status: "HIGH_EFFICIENCY", detail: "Ability to decompose complex systems into scalable micro-services." }
    ];

    const collegeReadiness = [
        { metric: "A-G REQS & ACADEMICS", value: "VERIFIED", note: "Proof of meeting A-G requirements and Academic Excellence" },
        { metric: "COLLEGE ESSAYS", value: "STATEMENT", note: "Personal Statement and Supplemental Essays", file: "/docs/College_Essays.pdf" },
        { metric: "ADMISSION", value: "ACCEPTED", note: "Copy of Official College Acceptance Letter", file: "/docs/Acceptance_Letter.pdf" },
        { metric: "INTENT", value: "BS COMP SCI", note: "Focus in Software Engineering & AI" }
    ];

    return (
        <section className="w-full min-h-screen bg-background flex flex-col relative z-0 selection:bg-accent selection:text-white overflow-hidden text-white pattern-dots">
            <style>{dossierStyles}</style>
            
            {/* Deep Muted Dither Background */}
            <div className="fixed inset-0 z-0 opacity-40 pointer-events-none mix-blend-screen overflow-hidden">
                <Dither
                    waveColor={[20/255, 60/255, 90/255]} 
                    colorNum={4}
                    waveAmplitude={0.5}
                    waveFrequency={1.5}
                    waveSpeed={0.02}
                />
            </div>

            {/* Global Noise Overlay */}
            <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.04] mix-blend-overlay" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.85\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")' }}></div>

            {/* 1. HERO SECTION - Strict Structure matching reference */}
             <motion.div style={{ y: yHero, opacity: opacityHero, scale: scaleHero }} className="p-6 md:p-12 lg:p-16 border-b-brut min-h-[85vh] flex flex-col justify-between relative z-10 pt-32 mx-auto max-w-[1920px] w-full bg-background/50 backdrop-blur-sm">
                
                {/* Back Nav - Simple Grey Pill per reference */}
                <div className="flex justify-start mb-8 md:mb-12">
                     <Link to="/leadership" className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-white/50 hover:text-white transition-all duration-300 flex items-center gap-3 bg-white/5 px-4 py-2 border border-transparent shadow-none group">
                        <span className="w-6 h-px bg-white/30 group-hover:w-8 group-hover:bg-white transition-all duration-300"></span>
                        SYS_RETURN
                    </Link>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-12 gap-12 xl:gap-0 mt-auto items-end w-full">
                    {/* Left Typography Block */}
                    <div className="xl:col-span-8 flex flex-col">
                        <motion.h1 
                            initial={{ opacity: 0, y: 40 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-[clamp(4.5rem,10vw,14rem)] font-heading font-black uppercase leading-[0.82] tracking-tighter text-foreground drop-shadow-2xl"
                        >
                            ABRAHAM<br />MORA-TADEO
                        </motion.h1>
                        <motion.h2 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                            className="text-[clamp(1.5rem,4vw,3.5rem)] font-heading font-extrabold uppercase leading-none tracking-tighter text-accent mt-6 drop-shadow-lg"
                        >
                            Software Engineer <span className="text-white/20 font-light px-2">&</span> UI Architect
                        </motion.h2>
                    </div>
                    
                    {/* Right Info Box Block - Hard bordered */}
                    <div className="xl:col-span-4 flex justify-end">
                         <motion.div 
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                            className="max-w-[400px] bg-black border border-border pb-8"
                        >
                             <div className="w-full h-8 border-b border-border bg-white/[0.02]"></div>
                             <div className="px-6 md:px-8 mt-6">
                                <p className="font-mono text-xs md:text-sm uppercase font-bold text-muted leading-relaxed tracking-wide mb-8">
                                    Engineering high-performance iOS and Web architectures, fused with brutalist UI systems. Focused on precision, scalability, and extreme aesthetic impact.
                                </p>
                                <div className="space-y-4 border-t border-white/10 pt-6">
                                    <div className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full bg-accent"></span>
                                        <span className="font-mono text-[9px] md:text-[10px] uppercase font-bold text-white/50 tracking-[0.2em]">SYS_STATUS: READY</span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full bg-accent shadow-[0_0_10px_rgba(56,189,248,0.8)]"></span>
                                        <span className="font-mono text-[9px] md:text-[10px] font-bold uppercase tracking-widest text-accent">NVIDIA CERTIFIED AI SPECIALIST</span>
                                    </div>
                                </div>
                             </div>
                        </motion.div>
                    </div>
                </div>
            </motion.div>

            {/* Main Content Areas */}
            <div className="relative z-20 max-w-[1920px] mx-auto w-full bg-background/80 backdrop-blur-md">
                
                {/* 2. ENGINEERING PORTFOLIO - Strict 1px Grid Architecture */}
                <div className="border-b-brut relative overflow-hidden">
                     {/* Section Header */}
                     <div className="p-6 md:p-12 lg:p-16 border-b-brut flex flex-col md:flex-row justify-between md:items-end gap-6 bg-black">
                        <div>
                            <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-accent flex items-center gap-4 mb-3">
                                <span className="w-8 h-px bg-accent"></span>
                                DEPLOYED_SYSTEMS
                            </h3>
                            <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter text-foreground leading-none">
                                ENGINEERING<br />PORTFOLIO
                            </h2>
                        </div>
                        <span className="font-heading text-6xl md:text-8xl lg:text-[10rem] font-black leading-none text-white/5 select-none -mb-8">01</span>
                    </div>

                    {/* Strict CSS Grid for Projects */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                            {engineeringPortfolio.map((project, idx) => (
                                <motion.a 
                                    href={project.link}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true, margin: "-100px" }}
                                    transition={{ duration: 0.6, delay: idx * 0.1 }}
                                    key={idx} 
                                    className={`block border-b-brut md:border-b-0 ${idx < 2 ? 'lg:border-r-brut' : ''} ${idx === 0 || idx === 1 ? 'md:border-b-brut lg:border-b-0' : ''} ${idx % 2 === 0 ? 'md:border-r-brut' : ''} p-8 md:p-12 bg-black hover:bg-white/[0.02] transition-colors duration-300 flex flex-col h-full relative group/card`}
                                >
                                    {/* Hover Top Accent Bar */}
                                    <div className="absolute top-0 left-0 w-full h-0 border-t-2 border-transparent group-hover/card:border-accent transition-colors duration-300"></div>

                                    <div className="flex justify-between items-start mb-8">
                                        <span className="font-mono text-[9px] font-bold uppercase tracking-[0.2em] text-accent bg-accent/5 border border-accent/20 px-2 py-1">{project.type}</span>
                                        <span className="text-muted group-hover/card:text-accent group-hover/card:translate-x-1 group-hover/card:-translate-y-1 transition-all">↗</span>
                                    </div>
                                    
                                    <h4 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold uppercase tracking-tighter text-foreground mb-4 leading-[0.9]">
                                        {project.name}
                                    </h4>
                                    <p className="font-mono text-[10px] md:text-xs leading-relaxed text-muted mb-10 uppercase flex-1 tracking-wide group-hover/card:text-white/80 transition-colors">
                                        {project.description}
                                    </p>
                                    
                                    <div className="mt-auto space-y-6">
                                        <div>
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block mb-3 border-b-brut pb-2">TECH_STACK</span>
                                            <div className="flex flex-wrap gap-2">
                                                {project.stack.map(tech => <span key={tech} className="font-mono text-[9px] font-bold text-white/60 bg-white/5 px-2 py-1 border border-white/5">{tech}</span>)}
                                            </div>
                                        </div>
                                        <div className="pt-4 border-t-brut flex flex-col gap-2">
                                            <span className="font-mono text-[9px] uppercase tracking-widest text-muted block">METRICS</span>
                                            <div className="flex flex-col gap-1">
                                                {project.metrics.map(metric => <span key={metric} className="font-mono text-[9px] font-bold text-accent tracking-tighter uppercase before:content-['■'] before:mr-2 before:text-[6px]">{metric}</span>)}
                                            </div>
                                        </div>
                                    </div>
                                </motion.a>
                            ))}
                        </div>
                </div>

                {/* 3. SENIOR PROJECT - Strict Horizontal Compartment */}
                <div className="border-b-brut bg-black">
                     <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 overflow-hidden relative">
                         {/* Optional tiny background visual for interest, kept strict to its block */}
                         <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-accent/5 to-transparent z-0 pointer-events-none"></div>

                        {/* Left Title Block - Full border constraints */}
                        <div className="col-span-1 lg:col-span-5 flex flex-col justify-center p-8 md:p-12 lg:p-16 border-b-brut lg:border-b-0 lg:border-r-brut relative z-10">
                            <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-accent flex items-center gap-4 mb-6">
                                <span className="w-8 h-px bg-accent"></span>
                                SENIOR_CAPSTONE
                            </h3>
                            <h2 className="font-heading text-5xl md:text-7xl font-black uppercase tracking-tighter leading-[0.85] mb-8 text-foreground drop-shadow-xl">
                                SCHURR<br />CHRONICLES
                            </h2>
                            <div className="flex flex-col gap-6 w-max">
                                <div className="flex items-center gap-3 border border-white/10 px-4 py-3 bg-white/5">
                                    <div className="w-2 h-2 bg-accent animate-pulse rounded-full shadow-[0_0_10px_rgba(56,189,248,1)]"></div>
                                    <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-foreground">{seniorProject.status}</span>
                                </div>
                                <a 
                                    href={seniorProject.url} 
                                    target="_blank" 
                                    rel="noreferrer"
                                    className="font-mono text-[10px] font-bold uppercase tracking-widest text-background bg-foreground px-6 py-4 hover:bg-accent hover:text-background transition-colors duration-300 text-center flex items-center gap-4 group justify-center"
                                >
                                    LAUNCH SITE <span className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform">↗</span>
                                </a>
                            </div>
                        </div>

                        {/* Right Detail Block - Dark background, constrained */}
                        <div className="col-span-1 lg:col-span-7 flex flex-col justify-center p-8 md:p-12 lg:p-16 relative z-10 bg-black/50">
                            <h4 className="font-heading text-3xl md:text-4xl font-extrabold uppercase tracking-tighter leading-none mb-6 text-foreground">
                                DIGITAL PUBLISHING PLATFORM
                            </h4>
                            <p className="font-mono text-xs md:text-sm font-bold uppercase leading-relaxed text-muted tracking-wide mb-12 max-w-2xl">
                                {seniorProject.description}
                            </p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 md:gap-12">
                                <div className="flex flex-col border-t-brut pt-6">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent block mb-4">CORE_TECHNOLOGIES</span>
                                    <ul className="space-y-3 font-mono text-[10px] font-bold uppercase tracking-wide">
                                        {seniorProject.technologies.map(tech => (
                                            <li key={tech} className="flex gap-3 items-center text-foreground/80">
                                                <span className="text-white/20">→</span> {tech}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className="flex flex-col border-t-brut pt-6">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent block mb-4">KEY_DELIVERABLES</span>
                                    <ul className="space-y-3 font-mono text-[10px] font-bold uppercase tracking-wide">
                                        {seniorProject.deliverables.map(item => (
                                            <li key={item} className="flex gap-3 items-center text-foreground/80">
                                                <span className="text-accent">■</span> {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-24">
                                {seniorProject.documents.map((doc, i) => (
                                    <motion.div 
                                        key={doc.title} 
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 1, delay: i * 0.2 }}
                                        className={`p-10 glass-morphism rounded-3xl group flex flex-col gap-6 ${i === 1 ? 'md:mt-32' : ''}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="w-6 h-px bg-accent/30"></span>
                                            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-accent/50">PROJECT_ARTIFACT</span>
                                        </div>
                                        <h4 className="font-heading text-3xl text-white uppercase tracking-tighter leading-none">{doc.title}</h4>
                                        <p className="font-mono text-[10px] text-muted uppercase tracking-tight leading-relaxed opacity-60">
                                            {doc.desc}
                                        </p>
                                        <a 
                                            href={doc.file} 
                                            download 
                                            className="mt-4 w-max flex items-center gap-4 px-8 py-3 bg-white/5 border border-white/10 rounded-full font-mono text-[10px] font-bold uppercase tracking-widest text-[#fafafa] hover:bg-accent hover:text-black hover:border-accent transition-all duration-500 group/btn"
                                        >
                                            DOWNLOAD FILE <span className="group-hover:translate-y-1 transition-transform">↓</span>
                                        </a>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 4. CAREER & COLLEGE READINESS - Cinematic Gallery */}
                <div className="relative bg-black py-32 overflow-hidden">
                    <AmbientGlow color="#38bdf8" opacity={0.05} size="800px" className="top-0 -left-1/4" />
                    
                    <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-24">
                        {/* Section Lead */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-end mb-32">
                            <div>
                                <h3 className="font-mono text-xs uppercase tracking-[0.5em] text-accent mb-8">/ PERFORMANCE_INDEX</h3>
                                <h2 className="font-heading text-6xl md:text-9xl text-white uppercase tracking-tighter leading-[0.85]">CAREER<br />READINESS</h2>
                            </div>
                            <p className="font-mono text-sm md:text-base text-muted uppercase leading-relaxed max-w-xl self-end mb-4">
                                Strategic professional evolution and evidence of high-performance vocational preparedness.
                            </p>
                        </div>

                        {/* Career Floating Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 items-start">
                            {careerReadiness.map((item, i) => (
                                <div key={item.category} className={i % 2 === 1 ? 'lg:mt-32' : ''}>
                                    <div className="flex flex-col gap-8">
                                        <span className="font-heading text-6xl text-white/10">{i + 1 < 10 ? `0${i+1}` : i+1}</span>
                                        <div className="flex flex-col gap-6">
                                            <h4 className="font-heading text-4xl text-white uppercase tracking-tighter">{item.category}</h4>
                                            <p className="font-mono text-xs text-muted uppercase tracking-tight leading-relaxed">{item.detail}</p>
                                        </div>
                                        {item.file && (
                                            <GlassGalleryCard 
                                                title={item.category} 
                                                url={item.file} 
                                                desc="Validation of performance and readiness architecture." 
                                            />
                                        )}
                                        {!item.file && (
                                            <div className="p-8 rounded-2xl border border-white/5 bg-white/[0.02]">
                                                <span className="font-mono text-[10px] text-accent uppercase tracking-widest">{item.status}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Digital Vertical Spacer */}
                        <div className="h-64"></div>

                        {/* College Lead */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 items-end mb-32">
                            <div className="order-2 lg:order-1">
                                <p className="font-mono text-sm md:text-base text-muted uppercase leading-relaxed max-w-xl mb-4">
                                    The architectural foundation of academic excellence and collegiate placement data.
                                </p>
                            </div>
                            <div className="order-1 lg:order-2 lg:text-right">
                                <h3 className="font-mono text-xs uppercase tracking-[0.5em] text-accent mb-8">/ ACADEMIC_LEDGER</h3>
                                <h2 className="font-heading text-6xl md:text-9xl text-white uppercase tracking-tighter leading-[0.85]">COLLEGE<br />READINESS</h2>
                            </div>
                        </div>

                        {/* College Floating Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 items-start">
                            {collegeReadiness.map((item, i) => (
                                <div key={item.metric} className={i % 3 === 1 ? 'lg:mt-48' : i % 3 === 2 ? 'lg:mt-24' : ''}>
                                    <div className="flex flex-col gap-10 p-10 glass-morphism rounded-3xl group hover:scale-[1.02] transition-transform duration-700">
                                        <div className="flex flex-col gap-2">
                                            <span className="font-mono text-[10px] text-accent/50 uppercase tracking-[0.4em]">METRIC_ID: 0{i+1}</span>
                                            <h4 className="font-heading text-6xl text-white uppercase tracking-tighter leading-none">{item.value}</h4>
                                        </div>
                                        <div className="h-px w-12 bg-accent/20"></div>
                                        <p className="font-mono text-xs text-muted uppercase tracking-tight leading-relaxed">{item.note}</p>
                                        {item.file && (
                                            <GlassGalleryCard 
                                                title={item.metric} 
                                                url={item.file} 
                                                desc={`Validated academic credentials: ${item.metric}.`} 
                                            />
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                </div>
        </section>
    );
}
